import { useAttachmentBlobUrl } from '../../lib/attachmentCache';
import { Image as ImageIcon } from 'lucide-react';

/**
 * Single thumbnail tile. Renders the cached blob URL when ready,
 * falls back to a muted placeholder while loading.
 */
function Tile({ att, onClick, overlayText, data_testid }) {
  const url = useAttachmentBlobUrl(att.id);
  // Rendered as a <div role="button"> instead of a <button> because
  // consumers (e.g. the Kanban card) already wrap the whole card in a
  // <button> for "open drawer". HTML forbids nested interactive
  // elements; React will surface a hydration warning otherwise.
  const handleActivate = (e) => {
    e.stopPropagation();
    onClick?.();
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleActivate(e);
        }
      }}
      className="relative h-12 w-12 rounded-md overflow-hidden border border-border bg-muted/40 shrink-0 hover:ring-2 hover:ring-primary transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
      title={att.original_filename}
      data-testid={data_testid}
    >
      {url ? (
        <img
          src={url}
          alt={att.original_filename}
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex items-center justify-center h-full w-full">
          <ImageIcon className="h-4 w-4 text-muted-foreground" />
        </div>
      )}
      {overlayText && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-xs font-semibold pointer-events-none">
          {overlayText}
        </div>
      )}
    </div>
  );
}

/**
 * Compact inline preview shown on task cards. Renders up to `max`
 * image thumbnails (default 3). When more exist, the last tile is
 * darkened and labelled "+N" so ops sees there's more without
 * needing to open the view drawer.
 *
 * Clicks bubble back via `onOpenAt(index)` so the parent can launch
 * the lightbox or view drawer focused on that image.
 */
export default function TaskCardThumbnails({
  images = [],
  totalAttachments = 0,   // includes non-image attachments
  max = 3,
  onOpenAt,
  data_testid = 'task-card-thumbnails',
}) {
  if (!images || images.length === 0) return null;
  const visible = images.slice(0, max);
  const hiddenImageCount = Math.max(images.length - max, 0);
  // "+N more" counts every extra attachment (images we couldn't show
  // PLUS non-image attachments still hiding behind the paperclip
  // badge), so the user has one canonical number to look at.
  const extra = hiddenImageCount + Math.max(totalAttachments - images.length, 0);

  return (
    <div className="flex gap-1.5 mt-2" data-testid={data_testid}>
      {visible.map((att, idx) => {
        const isLastVisible = idx === visible.length - 1 && extra > 0;
        return (
          <Tile
            key={att.id}
            att={att}
            onClick={() => onOpenAt?.(idx)}
            overlayText={isLastVisible ? `+${extra}` : null}
            data_testid={`${data_testid}-tile-${idx}`}
          />
        );
      })}
    </div>
  );
}
